import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RichTextEditor from './components/RichTextEditor';
import PagePreviewPanel from './components/PagePreviewPanel';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import SidebarPanel from './components/SidebarPanel';
import CollaborationBar from './components/CollaborationBar';
import VersionHistory from './components/VersionHistory';
import { pagesService, pageVersionsService } from '../../lib/services';
import { supabase } from '../../lib/supabase';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AUTO_SAVE_DELAY = 3000;

const ContentEditor = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [content, setContent] = useState(`<h1>Welcome to ContentCraft CMS</h1>\n<p>Start creating your professional content with our advanced WYSIWYG editor. This powerful tool transforms your ideas into beautifully formatted web content without requiring any technical expertise.</p>\n<h2>Key Features</h2>\n<ul>\n<li>Real-time preview across all devices</li>\n<li>Collaborative editing with team members</li>\n<li>Built-in SEO optimization tools</li>\n<li>Version control and rollback capabilities</li>\n</ul>`);
  const [viewMode, setViewMode] = useState('split');
  const [deviceMode, setDeviceMode] = useState('desktop');
  const [sidebarTab, setSidebarTab] = useState('settings');
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const [pageId, setPageId] = useState(null);
  const [pageTitle, setPageTitle] = useState('Getting Started with ContentCraft');
  const [pageStatus, setPageStatus] = useState('draft');
  const [seoTitle, setSeoTitle] = useState('ContentCraft CMS - Professional Content Management Made Easy');
  const [seoDescription, setSeoDescription] = useState('Transform your content creation workflow with ContentCraft CMS.');
  const [seoKeywords, setSeoKeywords] = useState('content management, cms, wysiwyg editor, seo tools');

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const isDirtyRef = useRef(false);

  // Real versions from DB
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Real-time presence collaborators
  const [collaborators, setCollaborators] = useState([]);
  const presenceChannelRef = useRef(null);

  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-home', icon: 'Home' },
    { label: 'Content Editor', path: '/content-editor' }
  ];

  // Load page from URL param if present
  useEffect(() => {
    const id = searchParams?.get('pageId');
    if (id) {
      loadPage(id);
    }
  }, []);

  // Load versions when pageId is set
  useEffect(() => {
    if (pageId) {
      loadVersions(pageId);
    }
  }, [pageId]);

  // Supabase Realtime presence for collaboration
  useEffect(() => {
    const channelName = pageId ? `editor:${pageId}` : 'editor:new';
    const userName = userProfile?.full_name || userProfile?.email?.split('@')?.[0] || 'Anonymous';
    const userAvatar = userProfile?.avatar_url || null;

    const channel = supabase?.channel(channelName, {
      config: { presence: { key: userProfile?.id || 'anon' } }
    });

    channel?.on('presence', { event: 'sync' }, () => {
        const state = channel?.presenceState();
        const online = Object.values(state)?.flat()?.map((p) => ({
          id: p?.user_id || p?.presence_ref,
          name: p?.name || 'Anonymous',
          avatar: p?.avatar || null,
          avatarAlt: `Avatar for ${p?.name || 'Anonymous'}`,
          isActive: true
        }));
        setCollaborators(online);
      })?.on('presence', { event: 'join' }, ({ newPresences }) => {
        // sync handles the full state
      })?.on('presence', { event: 'leave' }, ({ leftPresences }) => {
        // sync handles the full state
      })?.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel?.track({
            user_id: userProfile?.id || 'anon',
            name: userName,
            avatar: userAvatar,
            online_at: new Date()?.toISOString()
          });
        }
      });

    presenceChannelRef.current = channel;

    return () => {
      channel?.unsubscribe();
    };
  }, [pageId, userProfile]);

  const loadPage = async (id) => {
    const { data, error } = await pagesService?.getById(id);
    if (!error && data) {
      setPageId(data?.id);
      setPageTitle(data?.title || '');
      setContent(data?.content || '');
      setPageStatus(data?.status || 'draft');
      setLastSavedAt(new Date(data?.updatedAt));
      setAutoSaveStatus('saved');
    }
  };

  const loadVersions = async (id) => {
    setLoadingVersions(true);
    const { data, error } = await pageVersionsService?.getByPageId(id);
    if (!error && data) {
      const mapped = data?.map((v, idx) => ({
        id: v?.id,
        author: v?.creator?.fullName || 'Unknown',
        timestamp: v?.createdAt ? new Date(v.createdAt) : new Date(),
        changes: v?.changeDescription || `Version ${v?.versionNumber || idx + 1}`,
        isCurrent: idx === 0
      }));
      setVersions(mapped);
    }
    setLoadingVersions(false);
  };

  // Auto-save logic
  const triggerAutoSave = useCallback(() => {
    isDirtyRef.current = true;
    setAutoSaveStatus('unsaved');

    if (autoSaveTimerRef?.current) {
      clearTimeout(autoSaveTimerRef?.current);
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      if (!isDirtyRef?.current) return;
      setAutoSaveStatus('saving');

      try {
        const payload = {
          title: pageTitle,
          content: content,
          status: pageStatus
        };

        let result;
        if (pageId) {
          result = await pagesService?.update(pageId, payload);
        } else {
          const slug = pageTitle?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '') || `draft-${Date.now()}`;
          result = await pagesService?.create({ ...payload, slug });
          if (!result?.error && result?.data?.id) {
            setPageId(result?.data?.id);
          }
        }

        if (result?.error) {
          setAutoSaveStatus('error');
        } else {
          isDirtyRef.current = false;
          setAutoSaveStatus('saved');
          setLastSavedAt(new Date());
        }
      } catch {
        setAutoSaveStatus('error');
      }
    }, AUTO_SAVE_DELAY);
  }, [content, pageTitle, pageStatus, pageId]);

  const handleContentChange = (newContent) => {
    setContent(newContent);
  };

  const handlePageTitleChange = (val) => {
    setPageTitle(val);
  };

  useEffect(() => {
    if (autoSaveStatus === null) return;
    triggerAutoSave();
    return () => {
      if (autoSaveTimerRef?.current) clearTimeout(autoSaveTimerRef?.current);
    };
  }, [content, pageTitle]);

  const handleFirstEdit = () => {
    if (autoSaveStatus === null) setAutoSaveStatus('unsaved');
  };

  const handleManualSave = async () => {
    if (autoSaveTimerRef?.current) clearTimeout(autoSaveTimerRef?.current);
    setAutoSaveStatus('saving');

    try {
      const payload = { title: pageTitle, content, status: pageStatus };
      let result;
      if (pageId) {
        result = await pagesService?.update(pageId, payload);
      } else {
        const slug = pageTitle?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '') || `draft-${Date.now()}`;
        result = await pagesService?.create({ ...payload, slug });
        if (!result?.error && result?.data?.id) setPageId(result?.data?.id);
      }

      if (result?.error) {
        setAutoSaveStatus('error');
      } else {
        isDirtyRef.current = false;
        setAutoSaveStatus('saved');
        setLastSavedAt(new Date());
      }
    } catch {
      setAutoSaveStatus('error');
    }
  };

  const handlePublish = async () => {
    setPageStatus('published');
    await handleManualSave();
  };

  const handleRestore = async (versionId) => {
    console.log('Restoring version:', versionId);
    setShowVersionHistory(false);
    if (pageId) loadVersions(pageId);
  };

  const handleOpenVersionHistory = () => {
    setShowVersionHistory(true);
    if (pageId) loadVersions(pageId);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <Header isCollapsed={sidebarCollapsed} userName={userProfile?.full_name || userProfile?.email?.split('@')?.[0] || 'Editor'} userRole={userProfile?.role || 'editor'} />

      <main className={`main-content with-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Top bar */}
        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 border-b border-border bg-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-3 md:mb-4">
            <div className="flex-1 min-w-0">
              <Breadcrumbs items={breadcrumbItems} />
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mt-2">
                Content Editor
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} />

              <button
                onClick={handleOpenVersionHistory}
                className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                <Icon name="History" size={18} />
                <span className="hidden md:inline">History</span>
              </button>

              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('edit')}
                  className={`px-2 md:px-3 py-1.5 rounded text-xs md:text-sm font-medium transition-colors ${viewMode === 'edit' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Icon name="Edit3" size={16} className="md:hidden" />
                  <span className="hidden md:inline">Edit</span>
                </button>
                <button
                  onClick={() => setViewMode('split')}
                  className={`px-2 md:px-3 py-1.5 rounded text-xs md:text-sm font-medium transition-colors ${viewMode === 'split' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Icon name="Columns" size={16} className="md:hidden" />
                  <span className="hidden md:inline">Split</span>
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-2 md:px-3 py-1.5 rounded text-xs md:text-sm font-medium transition-colors ${viewMode === 'preview' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Icon name="Eye" size={16} className="md:hidden" />
                  <span className="hidden md:inline">Preview</span>
                </button>
              </div>

              <Button
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                loading={autoSaveStatus === 'saving'}
                onClick={handleManualSave}>
                Save Draft
              </Button>
            </div>
          </div>

          <CollaborationBar
            collaborators={collaborators}
            comments={12}
            onToggleComments={() => {}} />
        </div>

        {/* Editor area */}
        <div className="flex flex-col lg:flex-row h-[calc(100vh-16rem)]">
          {(viewMode === 'edit' || viewMode === 'split') &&
          <div
            className={`flex flex-col ${viewMode === 'split' ? 'flex-1' : 'w-full'} border-r border-border overflow-hidden`}
            onClick={handleFirstEdit}>
            <RichTextEditor
              content={content}
              onChange={handleContentChange}
              placeholder="Start typing your content here..." />
          </div>
          }

          {(viewMode === 'preview' || viewMode === 'split') &&
          <PagePreviewPanel
            content={content}
            pageTitle={pageTitle}
            deviceMode={deviceMode}
            onDeviceModeChange={setDeviceMode} />
          }

          <SidebarPanel
            activeTab={sidebarTab}
            onTabChange={setSidebarTab}
            pageTitle={pageTitle}
            onPageTitleChange={handlePageTitleChange}
            pageStatus={pageStatus}
            onPageStatusChange={setPageStatus}
            seoTitle={seoTitle}
            onSeoTitleChange={setSeoTitle}
            seoDescription={seoDescription}
            onSeoDescriptionChange={setSeoDescription}
            seoKeywords={seoKeywords}
            onSeoKeywordsChange={setSeoKeywords}
            onPublish={handlePublish}
            onInsertMedia={() => {}} />
        </div>
      </main>

      {showVersionHistory &&
      <VersionHistory
        versions={versions}
        loading={loadingVersions}
        onRestore={handleRestore}
        onClose={() => setShowVersionHistory(false)} />
      }
    </div>
  );
};

export default ContentEditor;